import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface PriorityScrollableContentContextType {
  registerPriorityScrollableContentRef: (
    ref: React.RefObject<HTMLDivElement | null>,
  ) => () => void;
  activePriorityScrollableContentRef: React.RefObject<HTMLDivElement | null> | null;
}

const PriorityScrollableContentContext = createContext<
  PriorityScrollableContentContextType | undefined
>(undefined);

export const PriorityScrollableContentProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [refs, setRefs] = useState<React.RefObject<HTMLDivElement | null>[]>(
    [],
  );

  // Use useCallback to ensure these functions are stable and don't change on every render
  const registerScrollableRef = useCallback(
    (newRef: React.RefObject<HTMLDivElement | null>) => {
      setRefs((prev) => [newRef, ...prev]);
      return () => {
        setRefs((prev) => prev.filter((ref) => ref !== newRef));
      };
    },
    [],
  );

  const activeScrollableRef = useMemo(() => refs[0] || null, [refs]); // useMemo ensures the ref object is stable

  const value = useMemo(
    () => ({
      registerPriorityScrollableContentRef: registerScrollableRef,
      activePriorityScrollableContentRef: activeScrollableRef,
    }),
    [registerScrollableRef, activeScrollableRef],
  );

  return (
    <PriorityScrollableContentContext.Provider value={value}>
      {children}
    </PriorityScrollableContentContext.Provider>
  );
};

export const usePriorityScrollableContent =
  (): PriorityScrollableContentContextType => {
    const context = useContext(PriorityScrollableContentContext);
    if (!context) {
      // Return undefined or a default object that suits your use case
      return {
        activePriorityScrollableContentRef: null,
        registerPriorityScrollableContentRef: () => () => {
          // console.log(
          //   'registerPriorityScrollableContentRef is being called without a context provider!',
          // );
        },
      };
    }
    return context;
  };
